import { ComponentProps, useCallback, useMemo } from 'react';
import { TurnaroundSelectEntities } from './TurnaroundSelectEntities';
import { useTurnaroundDestinationEntities } from '../hooks/useTurnaroundDestinationEntities';
import { useTurnaroundDestinationSelectedEntities } from '../hooks/useTurnaroundDestinationSelectedEntities';
import { useDelivery } from 'api/hooks/useDelivery';
import { useTurnaroundOriginDeliveryId } from '../hooks/useTurnaroundOriginDeliveryId';

type Props = ComponentProps<'div'>;

export const TurnaroundSelectEntitiesDestination = ({ ...props }: Props) => {
  const [turnaroundDestinationEntities] = useTurnaroundDestinationEntities();
  const [turnaroundOriginDeliveryId] = useTurnaroundOriginDeliveryId();
  const [turnaroundDestinationSelectedEntities, setTurnaroundDestinationSelectedEntities] =
    useTurnaroundDestinationSelectedEntities();

  const { data: delivery, isLoading: deliveryLoading } = useDelivery(turnaroundOriginDeliveryId);

  const sets = useMemo(() => {
    const isPresentInDestinationSets = (setKitInstanceId: string) =>
      turnaroundDestinationEntities.some(
        entity => entity.type === 'set' && entity.setKitInstanceId === setKitInstanceId,
      );

    return (
      // members aren't entities themselves, they're listed so their consolidated set can nest them
      delivery?.sets.filter(
        set =>
          isPresentInDestinationSets(set.kitInstanceId) ||
          (!!set.consolidatedIntoKitInstanceId && isPresentInDestinationSets(set.consolidatedIntoKitInstanceId)),
      ) || []
    );
  }, [delivery?.sets, turnaroundDestinationEntities]);

  const implants = useMemo(() => {
    return (
      delivery?.implants?.filter(implant => {
        const isPresentInDestinationImplants = turnaroundDestinationEntities.some(
          entity => entity.type === 'implant' && entity.implantInstanceId === implant.implantInstanceId,
        );

        return isPresentInDestinationImplants;
      }) || []
    );
  }, [delivery?.implants, turnaroundDestinationEntities]);

  const onSelectSet = useCallback(
    (setKitInstanceId: string) => {
      // If it's already selected, remove it
      if (
        turnaroundDestinationSelectedEntities.some(
          entity => entity.type === 'set' && entity.setKitInstanceId === setKitInstanceId,
        )
      ) {
        setTurnaroundDestinationSelectedEntities(prev =>
          prev.filter(entity => entity.type !== 'set' || entity.setKitInstanceId !== setKitInstanceId),
        );
      } else {
        setTurnaroundDestinationSelectedEntities(prev => [...prev, { type: 'set', setKitInstanceId }]);
      }
    },
    [turnaroundDestinationSelectedEntities, setTurnaroundDestinationSelectedEntities],
  );

  const onSelectImplants = useCallback(() => {
    // If the implants are already selected, remove them, otherwise add them
    if (turnaroundDestinationSelectedEntities.some(entity => entity.type === 'implant')) {
      setTurnaroundDestinationSelectedEntities(prev => prev.filter(entity => entity.type !== 'implant'));
    } else {
      const implantsToAdd = delivery?.implants?.map(implant => ({
        type: 'implant' as const,
        implantInstanceId: implant.implantInstanceId,
      }));

      if (implantsToAdd) {
        setTurnaroundDestinationSelectedEntities(prev => [...prev, ...implantsToAdd]);
      }
    }
  }, [turnaroundDestinationSelectedEntities, delivery?.implants, setTurnaroundDestinationSelectedEntities]);

  return (
    <TurnaroundSelectEntities
      {...props}
      variant="destination"
      deliveryStatus={delivery?.status}
      sets={sets}
      implants={implants}
      selectedEntities={turnaroundDestinationSelectedEntities}
      isLoading={deliveryLoading}
      onSelectSet={onSelectSet}
      onSelectImplants={onSelectImplants}
    />
  );
};
